import React, { useMemo, useState } from "react";
import { Sparkles, TrendingUp, Users, Award, Calculator } from "lucide-react";
import { formatBC } from "@/lib/format";

/**
 * Business Income Simulator — replaces the old "Investment Calculator". Every
 * output is framed as an ESTIMATE based on sales activity, never as a
 * guaranteed return.
 *
 * Formula (illustrative, matches Partner Rewards page):
 *   retail_profit  = products_sold * avg_retail_price * (retail_margin_pct / 100)
 *   referral_income = referrals * avg_referral_bonus (₱)
 *   team_bonus     = team_volume * (avg_team_override_pct / 100)
 */
export default function Simulator() {
  const [inputs, setInputs] = useState({
    products_sold: 20,
    avg_retail_price: 800,
    retail_margin_pct: 18,
    referrals: 5,
    avg_referral_bonus: 500,
    team_volume: 25000,
    avg_team_override_pct: 5,
  });

  const upd = (k) => (e) =>
    setInputs((prev) => ({ ...prev, [k]: Number(e.target.value) || 0 }));

  const output = useMemo(() => {
    const retail = inputs.products_sold * inputs.avg_retail_price * (inputs.retail_margin_pct / 100);
    const referral = inputs.referrals * inputs.avg_referral_bonus;
    const team = inputs.team_volume * (inputs.avg_team_override_pct / 100);
    return { retail, referral, team, total: retail + referral + team };
  }, [inputs]);

  return (
    <div className="space-y-8" data-testid="simulator-page">
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold mb-3">
          <Sparkles size={12} /> Business Income Simulator
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight max-w-3xl">
          Model your monthly business as a{" "}
          <span className="text-[#FBBF24]">Zenith partner.</span>
        </h1>
        <p className="text-white/70 max-w-3xl mt-5 leading-relaxed">
          Adjust the sliders below to see estimated retail profit, referral income, and team bonuses.
          <strong className="text-[#FBBF24]"> All values are illustrative estimates only.</strong>{" "}
          Actual earnings depend on your sales performance, retail activity, and team volume.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Inputs */}
        <div className="glass-card p-6 space-y-5" data-testid="simulator-inputs">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold flex items-center gap-2">
            <Calculator size={12} /> Your Monthly Activity
          </div>

          <Field label="Products you sell per month" value={inputs.products_sold} min={0} step={1} onChange={upd("products_sold")} testid="sim-products-sold" />
          <Field label="Average retail price per product (₱)" value={inputs.avg_retail_price} min={0} step={50} onChange={upd("avg_retail_price")} testid="sim-avg-price" />
          <Field label="Your retail margin (%)" value={inputs.retail_margin_pct} min={0} max={50} step={1} onChange={upd("retail_margin_pct")} testid="sim-margin" hint="Starter: up to 10% · Builder: up to 18% · Executive: up to 25%" />
          <Field label="Direct referrals enrolled this month" value={inputs.referrals} min={0} step={1} onChange={upd("referrals")} testid="sim-referrals" />
          <Field label="Average referral bonus per enrollment (₱)" value={inputs.avg_referral_bonus} min={0} step={50} onChange={upd("avg_referral_bonus")} testid="sim-referral-bonus" hint="Illustrative: 10% of Business Starter Kit price" />
          <Field label="Team monthly product sales volume (₱)" value={inputs.team_volume} min={0} step={500} onChange={upd("team_volume")} testid="sim-team-volume" />
          <Field label="Average team override rate (%)" value={inputs.avg_team_override_pct} min={0} max={12} step={0.5} onChange={upd("avg_team_override_pct")} testid="sim-override-rate" hint="Weighted across 5 levels (illustrative: L1 5% · L2 3% · L3 2% · L4 1% · L5 1%)" />
        </div>

        {/* Outputs */}
        <div className="space-y-4" data-testid="simulator-outputs">
          <StatCard icon={TrendingUp} label="Estimated Retail Profit" value={output.retail} accent="gold" testid="sim-out-retail" />
          <StatCard icon={Users} label="Estimated Referral Income" value={output.referral} accent="blue" testid="sim-out-referral" />
          <StatCard icon={Award} label="Estimated Team Bonus" value={output.team} accent="emerald" testid="sim-out-team" />
          <div className="glass-card gold-border p-6" data-testid="sim-out-total">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">Total Estimated Monthly Earnings</div>
            <div className="font-mono-num text-4xl md:text-5xl font-black text-[#FBBF24] mt-2">
              ₱{formatBC(output.total)}
            </div>
            <div className="text-[11px] text-white/45 mt-2 uppercase tracking-[0.18em]">
              Illustrative estimate · Actual earnings depend on real sales performance
            </div>
          </div>
        </div>
      </div>

      <p className="text-center text-[11px] uppercase tracking-[0.22em] text-white/40 pt-4">
        Results are estimates only · No earnings are guaranteed · Zenith is a business platform, not an investment platform
      </p>
    </div>
  );
}

function Field({ label, value, onChange, min, max, step, testid, hint }) {
  return (
    <div>
      <label className="zfd-label">{label}</label>
      <input
        type="number"
        className="zfd-input"
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        step={step}
        data-testid={testid}
      />
      {hint && <div className="text-[10px] text-white/45 mt-1">{hint}</div>}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, testid }) {
  const map = {
    gold: "text-[#FBBF24]",
    blue: "text-[#93C5FD]",
    emerald: "text-emerald-300",
  };
  return (
    <div className="glass-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/50 font-semibold">
        <Icon size={14} /> {label}
      </div>
      <div className={`mt-2 font-mono-num text-3xl font-black ${map[accent]}`}>₱{formatBC(value)}</div>
    </div>
  );
}
